/*globals globalNavigate*/
import React, { useState, useEffect, useContext } from "react";
import {
  useNavigate,
  createSearchParams,
} from "react-router-dom";
import ActionsContext from "common/ActionsContext";
import SettingsView from "common/SettingsView";
import TextInput from "common/TextInput";
import NumberInput from "common/NumberInput";
import ArrangeInColumns from "common/layouts/ArrangeInColumns";
import ProfileContents from "./Contents";
import { importActionTriggers } from "./Contents/Import";
import { loadProfile } from "../api";

const doesNothing = () => {};

const ContextMenu = ({ options = null }) => {
  const actions = useContext(ActionsContext);
  const sends = (signal, ...args) => actions.send(signal, ...args);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li className="dropdown hidden-to-operators" key="help-menu">
        <a
          data-loading-effect="pulse"
          data-toggle="cardloading"
          title="Help"
          onClick={() => sends("open-settings-help")}
        >
          <i className="material-icons">help_outline</i>
        </a>
      </li>
      {options === null ? null : (
        <li className="dropdown hidden-to-operators" key="dropdown-menu">
          <a
            className="dropdown-toggle"
            data-toggle="dropdown"
            role="button"
            aria-haspopup="true"
            aria-expanded="false"
            title="Options"
          >
            <i className="material-icons">more_vert</i>
          </a>
          <ul className="dropdown-menu pull-right hidden-to-operators">
            {options.map(({ signal, method, ...settings }) => (
              <ActionMenuOption
                sends={sends}
                key={method ? `${signal}-${method}` : signal}
                {...settings}
                signal={signal}
                method={method}
              />
            ))}
          </ul>
        </li>
      )}
    </ul>
  );
};

const DefaultWrapper = ({ children, ...props }) => (
  <li {...props}>{children}</li>
);

export const ActionMenuOption = ({
  Wrapper = DefaultWrapper,
  sends,
  label,
  signal,
  ...params
}) => (
  <Wrapper>
    <a className="text-capitalize" onClick={() => sends(signal, params)}>
      {label}...
    </a>
  </Wrapper>
);

const optionsForEntities = ({
  cliName,
  addActionLabel,
  labelSingle,
  label,
  importable = false,
  customActions = [],
}) => [
  {
    signal: `show-create-${cliName}-modal`,
    label: `Add ${addActionLabel || labelSingle || label}`,
  },
  ...importActionTriggers({ cliName, label, labelSingle, importable }),
  ...customActions,
];

const settingsContextMenu = ({ fields }) => {
  const many = fields.filter(({ type }) => type === "many");
  const DefinedContextMenu = () => (
    <ContextMenu
      options={
        many.length === 0
          ? null
          : many.flatMap((entity) => optionsForEntities(entity))
      }
    />
  );
  return DefinedContextMenu;
};

const CANT_PROCEED_ERROR =
  "Cannot proceed while there are pending config changes." +
  " Try again later.";

const clearConfigAttempt = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const TextField = ({
  name,
  label,
  canUpdate = true,
  defaults = {},
  onChange = doesNothing,
}) => (
  <TextInput
    type="text"
    name={name}
    label={label}
    readOnly={canUpdate === false && defaults.stored === true}
    defaultValue={defaults[name]}
    onChange={onChange}
    className="input-control"
    labelClassName="input-group-addon"
  />
);

const NumberField = ({
  name,
  label,
  canUpdate = true,
  defaults = {},
  onChange = doesNothing,
}) => (
  <NumberInput
    name={name}
    label={label}
    readOnly={canUpdate === false && defaults.stored === true}
    defaultValue={defaults[name]}
    onChange={onChange}
    className="input-control"
    labelClassName="input-group-addon"
    hideArrows={true}
  />
);

const invalidFieldType = (type, { name }) => {
  throw new Error(`Invalid field type "${type}" for field ${name}.`);
};

const Field = ({ type, ...field }) =>
  type === "text" ? (
    <TextField {...field} />
  ) : type === "number" ? (
    <NumberField {...field} />
  ) : type === "many" ? (
    <ProfileContents {...field} />
  ) : (
    invalidFieldType(type, field)
  );

const manyAtTheEnd = (one, another) =>
  (one.type !== "many" && another.type !== "many") ||
  (one.type === "many" && another.type === "many")
    ? 0
    : one.type === "many"
    ? 1
    : another.type === "many"
    ? -1
    : 0;

const ShowProfileSettings = ({
  defaults,
  state,
  onChange = doesNothing,
  fields = [],
}) => (
  <ArrangeInColumns rowGap="0">
    {fields.toSorted(manyAtTheEnd).map(({ name, ...field }) => (
      <Field
        key={name}
        name={name}
        {...field}
        defaults={defaults}
        onChange={onChange}
      />
    ))}
  </ArrangeInColumns>
);

const createNewProfile = (fields = [], settings) =>
  Object.assign(
    Object.fromEntries(
      fields.map(({ name, type, defaultValue = "" }) => [
        name,
        type === "many" ? [] : defaultValue,
      ])
    ),
    settings
  );

const addId = (target) =>
  target === null ? null : { ...target, __id: target.__id || 0 };

const genericLoad = (name, cliType, fields) =>
  ifCl.run(`show profile ${cliType} `).then(loadProfile(name, fields));

export const checkNothingPending = () =>
  ifCl.run("show config diff").then((response) => {
    if (response.length > 0) {
      throw new Error(CANT_PROCEED_ERROR);
    }
  });

const notifyAndRevert = (error) => {
  clearConfigAttempt();
  throw error.message === undefined ? new Error(error) : error;
};

const submitCommand = (command) => ifCl.run(command, /*is batch*/ true);

const usePersistence = ({
  create = null,
  edit = null,
  loadProfile = genericLoad,
  cliType,
  fields = [],
  composeProfileSettings = doesNothing,
}) => {
  const apply = (settings, originalSettings = {}) => {
    const buildCommand = () =>
      composeProfileSettings(originalSettings, settings);

    return checkNothingPending()
      .then(buildCommand)
      .then(submitCommand)
      .catch(notifyAndRevert)
      .then(() => /*updated*/ settings);
  };
  const load =
    edit !== null
      ? (target) => loadProfile(target || edit, cliType, fields).then(addId)
      : create !== null
      ? () =>
          Promise.resolve(createNewProfile(fields, { stored: false })).then(
            addId
          )
      : () => Promise.resolve(null);

  return [load, apply];
};
const nonce = () => new Date().getTime();

export const ProfileSettings = ({
  typeLabel = "UNKNOWN",
  cliType,
  fields = [],
  edit = null,
  create = null,
  viewURL = "Unknown",
  returnView = undefined,
  ...settings
}) => {
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [load, apply] = usePersistence({
    create,
    edit,
    fields,
    cliType,
    ...settings,
  });
  const go = (path, queryParams={}, options={}) =>
    navigate({
      pathname: "/" + path,
      search: createSearchParams(queryParams ? queryParams : undefined).toString(),
    }, options);

  const doLoad = (target) => load(target).then(setTarget);

  const doLoadTargetName = ({ name }) => doLoad(name);

  useEffect(() => {
    doLoad();
  }, [edit, create]);

  const backToList = () => {
    go(viewURL);
  };

  const opensEditionTo = (target) => {
    if (target !== null) {
      go(viewURL, { edit: target.name }, { replace: true });
    }
    return target;
  };

  const editAfterApply = (target, ...args) =>
    apply(target, ...args)
      .then(opensEditionTo)
      .then(doLoadTargetName);
  const alreadyLoaded = () => Promise.resolve({ ...target, nonce: nonce() });

  return target === null ? null : (
    <>
      <SettingsView
        load={alreadyLoaded}
        apply={editAfterApply}
        close={returnView === undefined && backToList}
        returnView={returnView || viewURL}
        ContextMenu={settingsContextMenu({ fields })}
        title={`${target.stored ? "Edit" : "Add"} ${typeLabel} Profile`}
        applyActionClassName="hidden-to-operators"
        wrapIntoForm={false}
      >
        <ShowProfileSettings fields={fields} />
      </SettingsView>
    </>
  );
};

export default ProfileSettings;
